import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopTabbar from "../components/partials/TopTabbarIndex";
import Header from "../components/partials/Header";
import AccountDeletionService from '../services/AccountDeletionService';
import Swal from 'sweetalert2';

const AccountDeletionRequestPage = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [token, setToken] = useState('');
    const [tokenError, setTokenError] = useState('');
    const [currentRequestId, setCurrentRequestId] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        username: '',
        deletionReason: '',
        comment: '',
        confirmation: false
    });
    const [errors, setErrors] = useState({});

    // Récupérer les informations de l'utilisateur connecté
    const currentUser = JSON.parse(localStorage.getItem('user'));

    // Fonction pour masquer partiellement l'email - Affiche les 3 premiers caractères + points + @domaine
    const maskEmail = (email) => {
        if (!email) return '';

        const [localPart, domain] = email.split('@');
        if (!domain) return email;

        // Garder les 3 premiers caractères de la partie locale
        const prefix = localPart.substring(0, 3);

        // Remplacer le reste par des points
        const dots = '.'.repeat(Math.max(0, localPart.length - 3));

        return `${prefix}${dots}@${domain}`;
    };

    // Initialiser le formulaire avec les données de l'utilisateur si disponible
    useEffect(() => {
        if (currentUser) {
            setFormData(prev => ({
                ...prev,
                email: currentUser.email || '',
                username: currentUser.username || ''
            }));
        }
    }, [currentUser]);

    // Empêcher la fermeture de la modal avec la touche Echap ou le clic en dehors
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && showTokenModal) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        const handleClickOutside = (e) => {
            if (showTokenModal && e.target.classList.contains('modal-overlay')) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        if (showTokenModal) {
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('click', handleClickOutside);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('click', handleClickOutside);
            document.body.style.overflow = '';
        };
    }, [showTokenModal]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const handleTokenChange = (e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value) && value.length <= 6) {
            setToken(value);
            setTokenError('');
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'L\'adresse email est requise';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Adresse email invalide';
        }

        if (!formData.username) {
            newErrors.username = 'Le nom d\'utilisateur est requis';
        }

        if (!formData.deletionReason) {
            newErrors.deletionReason = 'Veuillez indiquer la raison de la suppression';
        }

        if (!formData.confirmation) {
            newErrors.confirmation = 'Vous devez confirmer votre demande';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateTokenInput = () => {
        if (!token || token.length !== 6) {
            setTokenError('Le token doit contenir 6 chiffres');
            return false;
        }
        if (!/^\d{6}$/.test(token)) {
            setTokenError('Le token doit contenir uniquement des chiffres');
            return false;
        }
        return true;
    };

    const handleValidateToken = async () => {
        if (!validateTokenInput()) {
            return;
        }

        setIsValidating(true);
        setTokenError('');

        try {
            const language = localStorage.getItem('language') || 'fr';
            const response = await AccountDeletionService.validateToken(token, language);

            console.log('=== TOKEN VALIDÉ AVEC SUCCÈS ===');
            console.log('Réponse:', response);
            console.log('================================');

            setShowTokenModal(false);
            setToken('');

            await Swal.fire({
                icon: 'success',
                title: 'Token validé !',
                text: 'Votre demande de suppression a été confirmée. Un administrateur va traiter votre demande sous 48h.',
                confirmButtonText: 'OK',
                confirmButtonColor: '#24afcc',
                allowOutsideClick: false,
                allowEscapeKey: false
            });

            navigate('/');

        } catch (error) {
            console.error('=== ERREUR VALIDATION TOKEN ===');
            console.error('Message:', error.message);
            console.error('Statut:', error.response?.status);
            console.error('Données:', error.response?.data);
            console.error('===============================');

            let errorMessage = 'Token invalide. Veuillez vérifier le code reçu par email.';

            if (error.response?.data?.message === 'TOKEN_EXPIRED') {
                errorMessage = 'Le token a expiré (15 minutes). Veuillez demander un nouveau token.';
            } else if (error.response?.data?.message === 'INVALID_TOKEN') {
                errorMessage = 'Token invalide. Veuillez vérifier le code à 6 chiffres reçu par email.';
            } else if (error.response?.data?.message === 'INVALID_REQUEST_STATUS') {
                errorMessage = 'Cette demande a déjà été traitée ou le token a déjà été validé.';
            }

            setTokenError(errorMessage);
        } finally {
            setIsValidating(false);
        }
    };

    const handleResendToken = async () => {
        setIsValidating(true);

        try {
            const language = localStorage.getItem('language') || 'fr';
            await AccountDeletionService.resendToken(formData.email, language);

            await Swal.fire({
                icon: 'success',
                title: 'Token renvoyé !',
                text: 'Un nouveau token a été envoyé à votre adresse email. Vérifiez votre boîte de réception.',
                confirmButtonText: 'OK',
                confirmButtonColor: '#24afcc',
                timer: 3000,
                timerProgressBar: true
            });

            setToken('');
            setTokenError('');

        } catch (error) {
            console.error('Erreur renvoi token:', error);

            let errorMessage = 'Impossible de renvoyer le token. Veuillez réessayer plus tard.';

            if (error.response?.data?.message === 'NO_PENDING_REQUEST') {
                errorMessage = 'Aucune demande en attente trouvée pour cet email.';
            } else if (error.response?.data?.message === 'EMAIL_SEND_ERROR') {
                errorMessage = 'Erreur lors de l\'envoi de l\'email. Veuillez réessayer.';
            }

            await Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: errorMessage,
                confirmButtonText: 'OK',
                confirmButtonColor: '#24afcc'
            });
        } finally {
            setIsValidating(false);
        }
    };

    const formatErrorMessage = (error, requestData) => {
        const isRequestAlreadyExists =
            error.response?.data?.message === 'REQUEST_ALREADY_EXISTS' ||
            error.message === 'REQUEST_ALREADY_EXISTS' ||
            (error.response?.data?.error === 'REQUEST_ALREADY_EXISTS');

        if (isRequestAlreadyExists) {
            const username = requestData?.username || formData.username;
            if (username) {
                return `Une demande de suppression a déjà été soumise pour l'utilisateur "${username}". Veuillez patienter, votre demande est en cours de traitement. Si vous avez des questions, contactez notre support.`;
            }
            return `Une demande de suppression a déjà été soumise pour ce compte. Veuillez patienter, votre demande est en cours de traitement. Si vous avez des questions, contactez notre support.`;
        }

        const isUserNotFound =
            error.response?.data?.message === 'USER_NOT_FOUND' ||
            error.message === 'USER_NOT_FOUND' ||
            (error.response?.data?.error === 'USER_NOT_FOUND');

        if (isUserNotFound) {
            const username = requestData?.username || formData.username;
            if (username) {
                return `Aucun utilisateur trouvé avec le nom d'utilisateur "${username}". Veuillez vérifier vos informations.`;
            }
            return `Aucun utilisateur trouvé. Veuillez vérifier que votre nom d'utilisateur est correct.`;
        }

        if (error.response?.data?.message === 'EMAIL_NOT_FOUND') {
            return `Aucun utilisateur trouvé avec cette adresse email. Veuillez vérifier vos informations.`;
        }

        if (error.response?.data?.message === 'INVALID_CREDENTIALS') {
            return 'Les informations fournies ne correspondent à aucun compte. Veuillez vérifier vos informations.';
        }

        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
            return error.response.data.errors.join(', ');
        }

        if (error.response?.status === 403) {
            return 'Vous n\'avez pas les autorisations nécessaires pour effectuer cette action. Veuillez vous connecter.';
        }

        if (error.response?.status === 404) {
            return 'Compte non trouvé. Veuillez vérifier que votre compte existe bien.';
        }

        if (error.response?.status === 400) {
            return 'Données invalides. Veuillez vérifier que tous les champs sont correctement remplis.';
        }

        if (error.response?.status === 409) {
            return 'Une demande est déjà en cours. Veuillez patienter ou contacter le support si le problème persiste.';
        }

        if (error.response?.data?.message && typeof error.response.data.message === 'string') {
            if (!error.response.data.message.includes('_') ||
                error.response.data.message === 'USER_NOT_FOUND' ||
                error.response.data.message === 'EMAIL_NOT_FOUND' ||
                error.response.data.message === 'REQUEST_ALREADY_EXISTS') {
                return formatErrorMessage({ ...error, response: { ...error.response, data: { message: null } } }, requestData);
            }
            return error.response.data.message;
        }

        return 'Une erreur est survenue lors de l\'envoi de votre demande. Veuillez réessayer plus tard.';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const requestData = {
            email: formData.email,
            username: formData.username,
            deletionReason: formData.deletionReason,
            comment: formData.comment,
            confirmation: formData.confirmation
        };

        console.log('=== JSON ENVOYÉ AU BACKEND ===');
        console.log('Données du formulaire:', formData);
        console.log('JSON complet:', JSON.stringify(requestData, null, 2));
        console.log('===============================');

        setIsSubmitting(true);

        try {
            const language = localStorage.getItem('language') || 'fr';
            const response = await AccountDeletionService.createRequest(requestData, language);

            console.log('=== RÉPONSE DU BACKEND ===');
            console.log('Succès:', response);
            console.log('Email retourné par le backend:', response.email);
            console.log('==========================');

            if (response) {
                if (response.id) {
                    setCurrentRequestId(response.id);
                }

                // Utiliser l'email de la réponse
                const emailFromResponse = response.email;

                await Swal.fire({
                    icon: 'success',
                    title: 'Demande envoyée !',
                    html: `Votre demande de suppression de compte a été envoyée avec succès.<br/><br/>
                           <strong>Un code de validation a été envoyé à l'adresse :</strong><br/>
                           ${maskEmail(emailFromResponse)}<br/><br/>
                           Veuillez saisir le code à 6 chiffres reçu par email pour confirmer votre demande.`,
                    confirmButtonText: 'Saisir le code',
                    confirmButtonColor: '#24afcc',
                    allowOutsideClick: false,
                    allowEscapeKey: false
                });

                // Mettre à jour l'email dans formData avec celui de la réponse
                setFormData(prev => ({
                    ...prev,
                    email: emailFromResponse
                }));

                setShowTokenModal(true);
            } else {
                throw new Error('Réponse invalide du serveur');
            }

        } catch (error) {
            console.error('=== ERREUR DÉTAILLÉE ===');
            console.error('Message:', error.message);
            console.error('Code erreur:', error.code);
            console.error('Statut:', error.response?.status);
            console.error('Données de l\'erreur:', error.response?.data);
            console.error('Requête envoyée:', requestData);
            console.error('========================');

            const userFriendlyMessage = formatErrorMessage(error, requestData);
            const isRequestAlreadyExists =
                error.response?.data?.message === 'REQUEST_ALREADY_EXISTS' ||
                error.message === 'REQUEST_ALREADY_EXISTS';

            await Swal.fire({
                icon: isRequestAlreadyExists ? 'info' : 'error',
                title: isRequestAlreadyExists ? 'Demande déjà existante' : 'Erreur',
                text: userFriendlyMessage,
                confirmButtonText: 'OK',
                confirmButtonColor: '#24afcc'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const reasons = [
        { value: 'Je n\'utilise plus l\'application', label: 'Je n\'utilise plus l\'application' },
        { value: 'Problèmes techniques', label: 'Problèmes techniques récurrents' },
        { value: 'Préoccupations de confidentialité', label: 'Préoccupations de confidentialité' },
        { value: 'Compte créé par erreur', label: 'Compte créé par erreur' },
        { value: 'Utilisation d\'un autre service', label: 'J\'utilise désormais un autre service' },
        { value: 'Autre', label: 'Autre raison' }
    ];

    const styles = `
        :root {
            --primary-color: #24afcc;
            --secondary-color: #29649c;
            --accent-color: #ffd700;
            --dark-color: #1a2a3a;
            --light-color: #f8f9fa;
            --danger-color: #dc3545;
            --success-color: #28a745;
            --warning-color: #ffc107;
            --info-color: #17a2b8;
            --gradient-primary: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
        }

        .full-width-container {
            width: 100%;
            max-width: 100%;
            margin-left: 0;
            margin-right: 0;
            padding-left: 0;
            padding-right: 0;
        }

        .hero-section {
            background: var(--gradient-primary);
            color: white;
            padding: 4rem 0;
            position: relative;
            overflow: hidden;
        }

        .hero-content {
            position: relative;
            z-index: 2;
        }

        .hero-title {
            font-size: 2.5rem;
            font-weight: 800;
            margin-bottom: 1rem;
            text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .hero-description {
            font-size: 1.1rem;
            margin-bottom: 0;
            opacity: 0.9;
        }

        .form-section {
            padding: 4rem 0;
            background: #f8f9fa;
            min-height: 60vh;
        }

        .form-container {
            background: white;
            border-radius: 16px;
            padding: 2.5rem;
            box-shadow: 0 10px 40px rgba(0,0,0,0.08);
            max-width: 800px;
            margin: 0 auto;
        }

        .warning-box {
            background: #fff3cd;
            border-left: 4px solid var(--warning-color);
            padding: 1.5rem;
            margin-bottom: 2rem;
            border-radius: 8px;
        }

        .warning-box i {
            color: var(--warning-color);
            font-size: 1.5rem;
        }

        .form-label {
            font-weight: 600;
            color: var(--dark-color);
            margin-bottom: 0.5rem;
        }

        .form-control, .form-select {
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 0.75rem 1rem;
            transition: all 0.3s ease;
        }

        .form-control:focus, .form-select:focus {
            border-color: var(--primary-color);
            box-shadow: 0 0 0 0.2rem rgba(36, 175, 204, 0.25);
        }

        .form-control.is-invalid, .form-select.is-invalid {
            border-color: var(--danger-color);
        }

        .invalid-feedback {
            color: var(--danger-color);
            font-size: 0.875rem;
            margin-top: 0.25rem;
        }

        .checkbox-label {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
            width: 20px;
            height: 20px;
            margin-top: 2px;
            cursor: pointer;
        }

        .checkbox-text {
            flex: 1;
            color: #555;
            line-height: 1.5;
        }

        .btn-submit {
            background: var(--gradient-primary);
            border: none;
            padding: 0.875rem 2rem;
            font-weight: 600;
            font-size: 1rem;
            border-radius: 8px;
            width: 100%;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
        }

        .btn-submit:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(36, 175, 204, 0.4);
        }

        .btn-submit:disabled {
            opacity: 0.7;
            cursor: not-allowed;
        }

        .spinner-border-sm {
            width: 1rem;
            height: 1rem;
            border-width: 0.2em;
        }

        .btn-cancel {
            background: #6c757d;
            border: none;
            padding: 0.875rem 2rem;
            font-weight: 600;
            font-size: 1rem;
            border-radius: 8px;
            width: 100%;
            margin-top: 1rem;
            transition: all 0.3s ease;
        }

        .btn-cancel:hover:not(:disabled) {
            background: #5a6268;
        }

        .btn-cancel:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.7);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 9999;
            backdrop-filter: blur(4px);
        }

        .modal-container {
            background: white;
            border-radius: 16px;
            width: 90%;
            max-width: 500px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            animation: modalSlideIn 0.3s ease-out;
        }

        @keyframes modalSlideIn {
            from {
                transform: translateY(-50px);
                opacity: 0;
            }
            to {
                transform: translateY(0);
                opacity: 1;
            }
        }

        .modal-header {
            background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
            color: white;
            padding: 1.5rem;
            border-radius: 16px 16px 0 0;
            text-align: center;
        }

        .modal-header h3 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
        }

        .modal-header p {
            margin: 0.5rem 0 0;
            opacity: 0.9;
            font-size: 0.875rem;
        }

        .modal-body {
            padding: 2rem;
        }

        .token-input-container {
            text-align: center;
            margin-bottom: 1.5rem;
        }

        .token-input {
            font-size: 2rem;
            text-align: center;
            letter-spacing: 0.5rem;
            font-weight: bold;
            padding: 0.75rem;
            border: 2px solid #dee2e6;
            border-radius: 12px;
            width: 100%;
            max-width: 200px;
            margin: 0 auto;
            display: block;
        }

        .token-input:focus {
            border-color: var(--primary-color);
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(36, 175, 204, 0.25);
        }

        .token-input.error {
            border-color: var(--danger-color);
        }

        .token-error {
            color: var(--danger-color);
            font-size: 0.875rem;
            margin-top: 0.5rem;
            text-align: center;
        }

        .token-info {
            background: #e7f3ff;
            padding: 1rem;
            border-radius: 8px;
            margin: 1rem 0;
            text-align: center;
            font-size: 0.875rem;
        }

        .token-info i {
            color: var(--primary-color);
            margin-right: 0.5rem;
        }

        .token-email {
            font-weight: bold;
        }

        .btn-validate {
            background: var(--gradient-primary);
            border: none;
            padding: 0.75rem;
            font-weight: 600;
            border-radius: 8px;
            width: 100%;
            margin-bottom: 0.75rem;
        }

        .btn-resend {
            background: transparent;
            border: 1px solid var(--primary-color);
            color: var(--primary-color);
            padding: 0.75rem;
            font-weight: 600;
            border-radius: 8px;
            width: 100%;
            transition: all 0.3s ease;
        }

        .btn-resend:hover:not(:disabled) {
            background: var(--primary-color);
            color: white;
        }

        .btn-validate:disabled, .btn-resend:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        @media (max-width: 768px) {
            .hero-title {
                font-size: 1.75rem;
            }
            
            .form-container {
                padding: 1.5rem;
                margin: 0 1rem;
            }

            .modal-body {
                padding: 1.5rem;
            }

            .token-input {
                font-size: 1.5rem;
                letter-spacing: 0.3rem;
            }
        }
    `;

    return (
        <div className="ip-scroll-container full-width-container">
            <style>{styles}</style>
            <TopTabbar />
            <Header />

            <section className="hero-section">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-12 text-center hero-content">
                            <h1 className="hero-title">Suppression de Compte</h1>
                            <p className="hero-description">
                                Nous sommes désolés de vous voir partir. Veuillez remplir ce formulaire pour demander la suppression de votre compte.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="form-section">
                <div className="container">
                    <div className="form-container">
                        <div className="warning-box">
                            <div className="d-flex">
                                <i className="fas fa-exclamation-triangle me-3"></i>
                                <div>
                                    <strong className="d-block mb-2">Attention :</strong>
                                    <p className="mb-0 small">
                                        La suppression de votre compte est irréversible. Toutes vos données personnelles,
                                        historique de transactions et informations associées seront définitivement supprimées
                                        de nos systèmes après validation de votre demande.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label htmlFor="email" className="form-label">
                                    Adresse Email <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="email"
                                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Votre adresse email"
                                    disabled={!!currentUser || isSubmitting}
                                />
                                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                            </div>

                            <div className="mb-4">
                                <label htmlFor="username" className="form-label">
                                    Nom d'utilisateur <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                                    id="username"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    placeholder="Votre nom d'utilisateur"
                                    disabled={!!currentUser || isSubmitting}
                                />
                                {errors.username && <div className="invalid-feedback">{errors.username}</div>}
                            </div>

                            <div className="mb-4">
                                <label htmlFor="deletionReason" className="form-label">
                                    Raison de la suppression <span className="text-danger">*</span>
                                </label>
                                <select
                                    className={`form-select ${errors.deletionReason ? 'is-invalid' : ''}`}
                                    id="deletionReason"
                                    name="deletionReason"
                                    value={formData.deletionReason}
                                    onChange={handleInputChange}
                                    disabled={isSubmitting}
                                >
                                    <option value="">Sélectionnez une raison...</option>
                                    {reasons.map(reason => (
                                        <option key={reason.value} value={reason.value}>
                                            {reason.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.deletionReason && <div className="invalid-feedback">{errors.deletionReason}</div>}
                            </div>

                            <div className="mb-4">
                                <label htmlFor="comment" className="form-label">
                                    Commentaire supplémentaire (optionnel)
                                </label>
                                <textarea
                                    className="form-control"
                                    id="comment"
                                    name="comment"
                                    rows="4"
                                    value={formData.comment}
                                    onChange={handleInputChange}
                                    placeholder="Partagez-nous vos commentaires ou suggestions pour nous aider à améliorer nos services..."
                                    disabled={isSubmitting}
                                ></textarea>
                            </div>

                            <div className="mb-4">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="confirmation"
                                        checked={formData.confirmation}
                                        onChange={handleInputChange}
                                        disabled={isSubmitting}
                                    />
                                    <span className="checkbox-text">
                                        <strong>Je confirme vouloir supprimer définitivement mon compte</strong><br />
                                        <small className="text-muted">
                                            Je comprends que cette action est irréversible et que toutes mes données seront supprimées.
                                        </small>
                                    </span>
                                </label>
                                {errors.confirmation && <div className="invalid-feedback d-block">{errors.confirmation}</div>}
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary btn-submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        <span>Envoi en cours...</span>
                                    </>
                                ) : (
                                    'Demander la suppression'
                                )}
                            </button>

                            <button
                                type="button"
                                className="btn btn-secondary btn-cancel"
                                onClick={() => navigate(-1)}
                                disabled={isSubmitting}
                            >
                                Annuler
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {showTokenModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <p>Un code à 6 chiffres a été envoyé à votre adresse email</p>
                        </div>
                        <div className="modal-body">
                            <div className="token-input-container">
                                <input
                                    type="text"
                                    className={`token-input ${tokenError ? 'error' : ''}`}
                                    value={token}
                                    onChange={handleTokenChange}
                                    placeholder="000000"
                                    maxLength="6"
                                    autoFocus
                                    disabled={isValidating}
                                />
                                {tokenError && <div className="token-error">{tokenError}</div>}
                            </div>

                            <div className="token-info">
                                <i className="fas fa-envelope"></i>
                                Un email contenant un code de validation a été envoyé à{' '}
                                <strong className="token-email">
                                    {maskEmail(formData.email)}
                                </strong>
                                <br />
                                <small>Le token est valable 15 minutes.</small>
                            </div>

                            <button
                                className="btn btn-primary btn-validate"
                                onClick={handleValidateToken}
                                disabled={isValidating || token.length !== 6}
                            >
                                {isValidating ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Validation...
                                    </>
                                ) : (
                                    'Valider le token'
                                )}
                            </button>

                            <button
                                className="btn btn-resend"
                                onClick={handleResendToken}
                                disabled={isValidating}
                            >
                                <i className="fas fa-redo-alt me-2"></i>
                                Renvoyer le token
                            </button>

                            <div className="text-center mt-3">
                                <small className="text-muted">
                                    Vous ne pouvez pas fermer cette fenêtre tant que vous n'avez pas validé votre token.
                                    <br />
                                    Si vous rencontrez des problèmes, veuillez contacter le support.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountDeletionRequestPage;